import React from 'react';
import 'twin.macro';
import { Domaine, Periode, Item } from '@teetsh/app/src/types/programmations';
import { HeaderItem } from '@teetsh/app/src/containers/Programmations/ProgV2/Print/components/HeaderItem';
import {
  IRow,
  Row,
} from '@teetsh/app/src/containers/Programmations/ProgV2/Print/components/Row';
import { MatiereLayout } from './components/MatiereLayout';
import _orderBy from '@teetsh/app/src/utils/arrays/orderBy';
import { RowTitleOrientation } from '@teetsh/app/src/containers/Programmations/ProgV2/Print/types';
import { Filler, Header, IHeader } from './types';
import { getPureReferentialItemName } from '@teetsh/app/src/components/Bo/ReferentialItemElement';

interface Props {
  matiereTitle?: string;
  domaines: Domaine[];
  periodes: Periode[];
  enforceNbColumns?: number;
  sectionTitle?: string;
  shortSectionTitle?: string;
  rowTitleOrientation: RowTitleOrientation;
  isFirst: boolean;
  preventCutting: boolean;
  breakBefore: boolean;
}

export function buildRows(
  domaines: Domaine[],
  periodes: Periode[],
  enforceNbColumns?: number,
): IRow[] {
  return periodes.map(p => {
    const blocks = domaines.map(d => ({
      items: _orderBy(
        d.periodContents
          .filter(pc => pc.periodeId === p.id)
          .flatMap(v => v.items),
        'y',
        ['asc'],
      ),
    }));

    const additionalBlocks = enforceNbColumns
      ? Array.from<{ items: Item[] }>({
          length: enforceNbColumns - domaines.length,
        }).fill({ items: [] })
      : [];

    return {
      header: {
        color: p.color,
        name: p.name,
        details: p.subtitle,
      },
      subrows: [
        {
          blocks: [...blocks, ...additionalBlocks],
        },
      ],
    };
  });
}

function buildHeaders(
  domaines: Domaine[],
  enforceNbColumns?: number,
): IHeader[] {
  const headers: Header[] = domaines.map(d => ({
    type: 'header',
    name: d.name,
    color: d.color,
  }));
  const fillers = enforceNbColumns
    ? Array.from<Filler>({ length: enforceNbColumns - domaines.length }).fill({
        type: 'filler',
      })
    : [];
  return [...headers, ...fillers];
}

export function DomainePeriodeView(props: Props) {
  const { domaines, periodes, enforceNbColumns, rowTitleOrientation } = props;
  const hasPeriodeSubtitles = periodes.some(p => p.subtitle);
  const rows = buildRows(domaines, periodes, enforceNbColumns);
  const headers = buildHeaders(domaines, enforceNbColumns);

  const areRowsEmpty = rows.every(r =>
    r.subrows.every(s =>
      (s.blocks || []).every(b => (b?.items || []).length === 0),
    ),
  );
  // Dans le cas d'un premier groupe même si c'est vide on affiche quand même
  // la structure, ça semble mieux que juste du vide
  if (areRowsEmpty && !props.isFirst) {
    return null;
  }

  return (
    <MatiereLayout
      matiereTitle={props.matiereTitle}
      hasFirstColSubtitles={hasPeriodeSubtitles}
      sectionTitle={props.sectionTitle}
      shortSectionTitle={props.shortSectionTitle}
      rowTitleOrientation={rowTitleOrientation}
      breakBefore={props.breakBefore && !props.isFirst}
    >
      {headers.map((d, idx) => {
        if (d.type === 'filler') {
          return <div key={idx} tw="flex-1 min-w-0" />;
        }
        return (
          <div key={idx} tw="flex-1 min-w-0">
            <HeaderItem
              value={getPureReferentialItemName(d.name)}
              color={d.color}
            />
          </div>
        );
      })}
      {rows.map((r, idx) => (
        <Row
          key={idx}
          subrows={r.subrows}
          header={r.header}
          hasFirstColSubtitles={hasPeriodeSubtitles}
          rowTitleOrientation={rowTitleOrientation}
          preventCutting={props.preventCutting}
          breakBefore={props.breakBefore}
        />
      ))}
    </MatiereLayout>
  );
}
